import { useState, useEffect, useMemo, Fragment } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, ChevronDown, ChevronRight, Lock, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (v === null || v === undefined ? '—' : v);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'medium' }) : '—');

const TxStatusBadge = ({ status }) => {
  const map = {
    posted:   { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'POSTED' },
    reversed: { cls: 'bg-rose-50 text-rose-700 border-rose-200',          label: 'REVERSED' },
  };
  const s = map[status] || { cls: 'bg-slate-100 text-slate-700 border-slate-200', label: status?.toUpperCase() || '—' };
  return <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${s.cls}`}>{s.label}</span>;
};

const SideTag = ({ side }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold ${
    side === 'debit' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
  }`}>{side === 'debit' ? 'DR' : 'CR'}</span>
);

const FinanceLedgerExplorerPage = () => {
  const { getAuthHeaders } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [entriesByTx, setEntriesByTx] = useState({}); // {tx_id: [entries]}
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({
    kind: '',
    status: 'all',
    operation_id: '',
    account_code: 'all',
    currency: 'all',
    from: '',
    to: '',
    search: '',
  });

  const fetchAccounts = async () => {
    try {
      const r = await axios.get(`${API}/finance/accounts`, { headers: getAuthHeaders() });
      setAccounts(r.data.accounts || []);
    } catch (e) { console.error(e); }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.kind) params.kind = filters.kind;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.operation_id) params.operation_id = filters.operation_id;
      params.limit = 200;
      const r = await axios.get(`${API}/finance/transactions`, { headers: getAuthHeaders(), params });
      setTransactions(r.data.transactions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); fetchTransactions(); /* eslint-disable-next-line */ }, []);

  const toggleExpand = async (tx) => {
    const next = !expanded[tx.id];
    setExpanded((p) => ({ ...p, [tx.id]: next }));
    if (next && !entriesByTx[tx.id]) {
      try {
        const r = await axios.get(`${API}/finance/transactions/${tx.id}`, { headers: getAuthHeaders() });
        setEntriesByTx((p) => ({ ...p, [tx.id]: r.data.entries || [] }));
      } catch (e) { console.error(e); }
    }
  };

  const filteredTx = useMemo(() => {
    return transactions.filter((tx) => {
      if (filters.from && tx.created_at < filters.from) return false;
      if (filters.to && tx.created_at > filters.to + 'T23:59:59') return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        if (!(
          (tx.id || '').toLowerCase().includes(s) ||
          (tx.kind || '').toLowerCase().includes(s) ||
          (tx.description || '').toLowerCase().includes(s) ||
          (tx.operation_id || '').toLowerCase().includes(s)
        )) return false;
      }
      // currency / account filters require entries — apply after on-demand fetch
      return true;
    });
  }, [transactions, filters.from, filters.to, filters.search]);

  return (
    <div className="space-y-6" data-testid="finance-ledger-explorer-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-indigo-600" />
            Libro Mayor · Ledger Explorer
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Vista append-only e inmutable del libro diario de partida doble.
            Cada transacción agrupa entries con débito y crédito balanceados.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Read-only
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Append-only
          </Badge>
          <Button size="sm" variant="outline" onClick={fetchTransactions} data-testid="ledger-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Buscar (id / kind / op / desc)</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="texto libre…"
                className="pl-8"
                data-testid="ledger-search-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Tipo (kind)</label>
            <Input
              value={filters.kind}
              onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
              placeholder="ej. remittance.payment"
              data-testid="ledger-kind-input"
              onBlur={fetchTransactions}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Estado</label>
            <Select
              value={filters.status}
              onValueChange={(v) => { setFilters({ ...filters, status: v }); setTimeout(fetchTransactions, 0); }}
            >
              <SelectTrigger data-testid="ledger-status-select"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="posted">posted</SelectItem>
                <SelectItem value="reversed">reversed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Operation ID</label>
            <Input
              value={filters.operation_id}
              onChange={(e) => setFilters({ ...filters, operation_id: e.target.value })}
              placeholder="op_…"
              data-testid="ledger-opid-input"
              onBlur={fetchTransactions}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Cuenta</label>
            <Select
              value={filters.account_code}
              onValueChange={(v) => setFilters({ ...filters, account_code: v })}
            >
              <SelectTrigger data-testid="ledger-account-select"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {accounts.map((a) => (
                  <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Moneda</label>
            <Select
              value={filters.currency}
              onValueChange={(v) => setFilters({ ...filters, currency: v })}
            >
              <SelectTrigger data-testid="ledger-currency-select"><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="UYU">UYU</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Desde</label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="ledger-from-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Hasta</label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="ledger-to-input" />
          </div>
        </div>
      </Card>

      {/* Transactions table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredTx.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Sin transacciones que mostrar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left">
                  <th className="p-3 font-medium text-slate-600 w-8"></th>
                  <th className="p-3 font-medium text-slate-600">ID</th>
                  <th className="p-3 font-medium text-slate-600">Kind</th>
                  <th className="p-3 font-medium text-slate-600">Estado</th>
                  <th className="p-3 font-medium text-slate-600">Actor</th>
                  <th className="p-3 font-medium text-slate-600">Operation</th>
                  <th className="p-3 font-medium text-slate-600">Descripción</th>
                  <th className="p-3 font-medium text-slate-600">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {filteredTx.map((tx) => {
                  const isOpen = expanded[tx.id];
                  const entries = entriesByTx[tx.id] || [];
                  const accountMatch = filters.account_code === 'all' || (entries.length > 0 && entries.some((e) => e.account_code === filters.account_code));
                  const currencyMatch = filters.currency === 'all' || (entries.length > 0 && entries.some((e) => e.currency === filters.currency));
                  // Visibility filter only applied once entries loaded; otherwise default visible.
                  const visible = (!isOpen) ? true : (accountMatch && currencyMatch);
                  if (!visible) return null;
                  return (
                    <Fragment key={tx.id}>
                      <tr
                        className="border-b hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleExpand(tx)}
                        data-testid={`ledger-tx-row-${tx.id}`}
                      >
                        <td className="p-3">
                          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </td>
                        <td className="p-3 font-mono text-xs text-slate-700">{tx.id.slice(0, 8)}…</td>
                        <td className="p-3"><span className="font-medium text-slate-800">{tx.kind}</span></td>
                        <td className="p-3"><TxStatusBadge status={tx.status} /></td>
                        <td className="p-3 text-slate-500 text-xs">{tx.actor_role}<br /><span className="font-mono">{tx.actor_id?.slice(0, 8)}…</span></td>
                        <td className="p-3 font-mono text-xs text-slate-600">{tx.operation_id ? `${tx.operation_id.slice(0, 8)}…` : '—'}</td>
                        <td className="p-3 text-slate-600 max-w-md truncate">{tx.description || '—'}</td>
                        <td className="p-3 text-slate-400 whitespace-nowrap text-xs">{fmtDate(tx.created_at)}</td>
                      </tr>
                      {isOpen && (
                        <tr key={`${tx.id}-entries`} className="bg-slate-50/60 border-b">                          <td></td>
                          <td colSpan={7} className="p-4">
                            <div className="border rounded-lg bg-white overflow-hidden">
                              <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                                <div className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                                  <Lock className="w-3 h-3" />
                                  Entries inmutables — {entries.length} líneas
                                </div>
                                {tx.reverses_tx_id && (
                                  <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Reverso de {tx.reverses_tx_id.slice(0, 8)}…
                                  </Badge>
                                )}
                              </div>
                              {entries.length === 0 ? (
                                <div className="p-3 text-slate-400 text-xs">Cargando entries…</div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead className="bg-slate-50/50">
                                    <tr className="text-left border-b">
                                      <th className="p-2 font-medium text-slate-500">Cuenta</th>
                                      <th className="p-2 font-medium text-slate-500">Tipo</th>
                                      <th className="p-2 font-medium text-slate-500">Lado</th>
                                      <th className="p-2 font-medium text-slate-500 text-right">Débito</th>
                                      <th className="p-2 font-medium text-slate-500 text-right">Crédito</th>
                                      <th className="p-2 font-medium text-slate-500">Moneda</th>
                                      <th className="p-2 font-medium text-slate-500">Desc.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entries.map((e) => (
                                      <tr key={e.id} className="border-b last:border-0">
                                        <td className="p-2 font-mono text-slate-800">{e.account_code}</td>
                                        <td className="p-2 text-slate-500">{e.account_type}</td>
                                        <td className="p-2"><SideTag side={e.side} /></td>
                                        <td className="p-2 text-right font-mono text-blue-700">{e.side === 'debit' ? e.amount : ''}</td>
                                        <td className="p-2 text-right font-mono text-amber-700">{e.side === 'credit' ? e.amount : ''}</td>
                                        <td className="p-2 text-slate-600">{e.currency}</td>
                                        <td className="p-2 text-slate-500">{e.description || '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {tx.fx_rate_snapshot && Object.keys(tx.fx_rate_snapshot || {}).length > 0 && (
                                <div className="px-3 py-2 border-t bg-slate-50 text-[11px] text-slate-600">
                                  <span className="font-semibold">FX snapshot:</span> <span className="font-mono">{JSON.stringify(tx.fx_rate_snapshot)}</span>
                                </div>
                              )}
                              {tx.metadata && Object.keys(tx.metadata || {}).length > 0 && (
                                <div className="px-3 py-2 border-t bg-slate-50 text-[11px] text-slate-600">
                                  <span className="font-semibold">Metadata:</span> <span className="font-mono break-all">{JSON.stringify(tx.metadata)}</span>
                                </div>
                              )}
                              <div className="px-3 py-2 border-t bg-slate-50 text-[11px] text-slate-500 flex flex-wrap gap-x-4">
                                <span><span className="font-semibold">Posted:</span> {fmtDate(tx.posted_at)}</span>
                                {tx.reversed_at && <span><span className="font-semibold">Reversed:</span> {fmtDate(tx.reversed_at)}</span>}
                                {tx.reversed_by_tx_id && <span><span className="font-semibold">Reversed by:</span> <span className="font-mono">{tx.reversed_by_tx_id.slice(0, 8)}…</span></span>}
                                <span><span className="font-semibold">Tx ID:</span> <span className="font-mono">{tx.id}</span></span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500 flex items-center justify-between">
          <span>{filteredTx.length} transacciones · solo lectura · ediciones no permitidas</span>
          <span className="text-slate-400">{fmt(filters.search) === '—' ? '' : `filtro activo: "${filters.search}"`}</span>
        </div>
      </Card>
    </div>
  );
};

export default FinanceLedgerExplorerPage;
